'use client'

import { create } from 'zustand'
import type { ActivityDto } from './types'
import { isOpenState } from './types'

/**
 * The dock's per-viewer state: is the drawer open, is the bar minimised, and which finished
 * activities has this person dismissed. The last two are conveniences of one person on one device,
 * so they live in localStorage (every read and write in try/catch: it throws in a private window and
 * with site data blocked, and the dock must work without it). Nothing here is shared or load-bearing:
 * a lost dismissal shows a finished job again for the few minutes it lasts.
 */
export const DOCK_STORAGE_KEY = 'hf-activity-dock:v1'
/** A dismissal is forgotten after this long; the activity it names is gone from the dock by then. */
const DISMISSAL_TTL_MS = 48 * 60 * 60 * 1000

interface Persisted {
  minimized: boolean
  /** activity id -> the instant it ended when dismissed (so a run that starts again is shown again) + when dismissed. */
  dismissed: Record<string, { ended: string; at: number }>
}

function read(): Persisted {
  const empty: Persisted = { minimized: false, dismissed: {} }
  try {
    const raw = typeof window === 'undefined' ? null : window.localStorage.getItem(DOCK_STORAGE_KEY)
    if (!raw) return empty
    const parsed = JSON.parse(raw) as Partial<Persisted>
    const now = Date.now()
    const dismissed: Persisted['dismissed'] = {}
    for (const [id, v] of Object.entries(parsed.dismissed ?? {})) {
      if (v && typeof v.ended === 'string' && typeof v.at === 'number' && now - v.at < DISMISSAL_TTL_MS) dismissed[id] = v
    }
    return { minimized: parsed.minimized === true, dismissed }
  } catch {
    return empty
  }
}

function write(p: Persisted): void {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(DOCK_STORAGE_KEY, JSON.stringify(p))
  } catch {
    // private window, blocked storage or a full quota: the dock works without it
  }
}

interface DockState extends Persisted {
  open: boolean
  /**
   * The card that is open in the drawer: an id, null for none, undefined for "the first one that is
   * going" (the default until somebody chooses). openDock(id) opens the drawer on that card.
   */
  expandedId: string | null | undefined
  openDock: (id?: string) => void
  closeDock: () => void
  /** Open this card, or close it when it is the one open now (`current`: what the drawer is showing, the default included). */
  toggleExpanded: (id: string, current: string | null) => void
  setMinimized: (minimized: boolean) => void
  dismiss: (activity: ActivityDto) => void
}

export const useActivityDockStore = create<DockState>((set, get) => ({
  ...read(),
  open: false,
  expandedId: undefined,
  openDock: (id) => set({ open: true, expandedId: id ?? get().expandedId }),
  closeDock: () => set({ open: false }),
  toggleExpanded: (id, current) => set({ expandedId: current === id ? null : id }),
  setMinimized: (minimized) => {
    set({ minimized })
    write({ minimized, dismissed: get().dismissed })
  },
  dismiss: (activity) => {
    if (isOpenState(activity.state)) return // work that is going on cannot be dismissed
    const dismissed = { ...get().dismissed, [activity.id]: { ended: activity.finishedAt ?? '', at: Date.now() } }
    set({ dismissed })
    write({ minimized: get().minimized, dismissed })
  },
}))

/**
 * What the dock lists: everything except finished work this person dismissed. A dismissal holds
 * only while the activity ended at the same instant: a run retried and ended again is shown again.
 */
export function visibleActivities(list: readonly ActivityDto[], dismissed: Persisted['dismissed']): ActivityDto[] {
  return list.filter((a) => {
    if (isOpenState(a.state)) return true
    const d = dismissed[a.id]
    return !d || d.ended !== (a.finishedAt ?? '')
  })
}
